import { useState, useEffect } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { getEmailPreferences, updateEmailPreferences } from '../../../services/emailNotificationService';

const NotificationPreferences = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [preferences, setPreferences] = useState({
    engagement: {
      newComments: true,
      newLikes: true,
      newShares: false,
      mentions: true,
      directMessages: true
    },
    campaigns: {
      campaignStart: true,
      campaignComplete: true,
      scheduledPosts: false,
      postPublished: true
    },
    analytics: {
      weeklyReport: true,
      monthlyReport: true,
      performanceAlerts: true,
      goalAchieved: true
    },
    system: {
      accountSecurity: true,
      platformUpdates: false,
      maintenanceAlerts: true,
      billingUpdates: true
    }
  });

  const [emailEnabled, setEmailEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(false);

  // Load email preferences from database
  useEffect(() => {
    if (user?.id) {
      loadEmailPreferences();
    }
  }, [user?.id]);

  const loadEmailPreferences = async () => {
    try {
      setLoading(true);
      const prefs = await getEmailPreferences(user?.id);
      
      // Update state with database values
      setPreferences({
        engagement: {
          newComments: true,
          newLikes: true,
          newShares: false,
          mentions: prefs?.brandMentionAlerts,
          directMessages: true
        },
        campaigns: {
          campaignStart: prefs?.campaignNotifications,
          campaignComplete: prefs?.campaignNotifications,
          scheduledPosts: false,
          postPublished: true
        },
        analytics: {
          weeklyReport: prefs?.weeklyDigest,
          monthlyReport: prefs?.monthlyReport,
          performanceAlerts: prefs?.performanceReports,
          goalAchieved: true
        },
        system: {
          accountSecurity: true,
          platformUpdates: false,
          maintenanceAlerts: true,
          billingUpdates: true
        }
      });
    } catch (error) {
      console.error('Error loading email preferences:', error);
      setMessage({ type: 'error', text: 'Failed to load preferences' });
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category, key, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [key]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      // Save email preferences to database
      await updateEmailPreferences(user?.id, {
        brandMentionAlerts: preferences?.engagement?.mentions,
        performanceReports: preferences?.analytics?.performanceAlerts,
        campaignNotifications: preferences?.campaigns?.campaignStart,
        threatAlerts: true,
        trendAlerts: true,
        weeklyDigest: preferences?.analytics?.weeklyReport,
        monthlyReport: preferences?.analytics?.monthlyReport,
        emailFrequency: 'real_time'
      });

      setMessage({ type: 'success', text: 'Preferences saved successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to save preferences' });
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    {
      id: 'engagement',
      title: 'Engagement Notifications',
      description: 'Get notified about interactions on your posts',
      icon: 'MessageSquare',
      items: [
        { key: 'newComments', label: 'New comments on posts' },
        { key: 'newLikes', label: 'New likes and reactions' },
        { key: 'newShares', label: 'Post shares and reposts' },
        { key: 'mentions', label: 'Brand mentions' },
        { key: 'directMessages', label: 'Direct messages' }
      ]
    },
    {
      id: 'campaigns',
      title: 'Campaign Updates',
      description: 'Stay informed about your campaign activities',
      icon: 'Target',
      items: [
        { key: 'campaignStart', label: 'Campaign started' },
        { key: 'campaignComplete', label: 'Campaign completed' },
        { key: 'scheduledPosts', label: 'Scheduled post reminders' },
        { key: 'postPublished', label: 'Post published successfully' }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Reports',
      description: 'Receive performance insights and reports',
      icon: 'BarChart3',
      items: [
        { key: 'weeklyReport', label: 'Weekly performance summary' },
        { key: 'monthlyReport', label: 'Monthly analytics report' },
        { key: 'performanceAlerts', label: 'Performance alerts' },
        { key: 'goalAchieved', label: 'Goal achievement notifications' }
      ]
    },
    {
      id: 'system',
      title: 'System Notifications',
      description: 'Important updates about your account',
      icon: 'Bell',
      items: [
        { key: 'accountSecurity', label: 'Security alerts' },
        { key: 'platformUpdates', label: 'Platform updates' },
        { key: 'maintenanceAlerts', label: 'Maintenance notifications' },
        { key: 'billingUpdates', label: 'Billing and subscription updates' }
      ]
    }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Notification Preferences</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Choose how you want to receive notifications
          </p>
        </div>
        <Button variant="default" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg ${message?.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {message?.text}
        </div>
      )}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
        <h3 className="text-sm font-semibold text-foreground mb-3">Notification Channels</h3>
        <div className="space-y-3">
          <Checkbox
            label="Email Notifications"
            description="Receive notifications via email"
            checked={emailEnabled}
            onChange={(e) => setEmailEnabled(e?.target?.checked)}
          />
          <Checkbox
            label="Push Notifications"
            description="Get browser push notifications"
            checked={pushEnabled}
            onChange={(e) => setPushEnabled(e?.target?.checked)}
          />
          <Checkbox
            label="SMS Notifications"
            description="Receive important alerts via SMS"
            checked={smsEnabled}
            onChange={(e) => setSmsEnabled(e?.target?.checked)}
          />
        </div>
      </div>
      <div className="space-y-6">
        {categories?.map((category) => (
          <div key={category?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={category?.icon} size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-foreground">
                  {category?.title}
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {category?.description}
                </p>
              </div>
            </div>

            <div className="space-y-3 ml-13">
              {category?.items?.map((item) => (
                <Checkbox
                  key={item?.key}
                  label={item?.label}
                  checked={preferences?.[category?.id]?.[item?.key]}
                  onChange={(e) => handleCategoryChange(category?.id, item?.key, e?.target?.checked)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-warning/10 rounded-lg border border-warning/20">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">
              Important Security Notifications
            </p>
            <p className="text-xs text-muted-foreground">
              Security alerts and account activity notifications cannot be disabled to protect your account. You will always receive these via email.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;